<template>
  <v-app class="app-layout">
    <!-- Header Block -->
    <app-header
      :logo-src="logoSrc"
      :dark-logo-src="darkLogoSrc"
      :logo-alt="logoAlt"
      :show-product-switcher="products && products.length > 0"
      :products="products"
      :user-name="userName"
      :user-avatar="userAvatar"
      :user-avatar-color="userAvatarColor"
      :user-menu-items="userMenuItems"
      :show-theme-switcher="showThemeSwitcher"
      :show-search="showSearch"
      :search-items="searchItems"
      :search-loading="searchLoading"
      :search-placeholder="searchPlaceholder"
      :search-item-text="searchItemText"
      :search-item-value="searchItemValue"
      @user-menu-click="handleUserMenuClick"
      @product-click="handleProductClick"
      @theme-change="handleThemeChange"
      @search-input="handleSearchInput"
      @search-select="handleSearchSelect"
    >
      <template v-if="$slots.search" #search>
        <slot name="search" />
      </template>
      <template v-if="$scopedSlots['search-item']" #search-item="{ item }">
        <slot name="search-item" :item="item" />
      </template>
    </app-header>

    <!-- Top Navigation Strip -->
    <top-nav-strip
      :items="topNavItems"
      @item-click="handleTopNavClick"
    >
      <template #default>
        <slot name="top-nav" />
      </template>
    </top-nav-strip>

    <!-- Main Content Area with Two-Column Layout -->
    <v-main class="app-layout__main">
      <div class="app-layout__wrapper">
        <!-- Skip to main content link -->
        <a href="#main-content" class="app-layout__skip-link">Skip to main content</a>

                <!-- Side Navigation Column -->
        <side-nav
          v-if="showSideNav"
          :items="sideNavItems"
          :width="sideNavWidth"
          @item-click="handleSideNavClick"
          @item-toggle="handleSideNavToggle"
        >
          <template #header>
            <slot name="sidenav-header" />
          </template>
          <template #footer>
            <slot name="sidenav-footer" />
          </template>
        </side-nav>

        <!-- Content Column -->
        <main class="app-layout__content-column" id="main-content" role="main">
          <v-container
            :fluid="fluidContent"
            :class="contentClass"
            class="app-layout__container"
          >
        <!-- Breadcrumbs -->
        <div v-if="showBreadcrumbs && breadcrumbs.length > 0" class="app-layout__breadcrumbs">
          <slot name="breadcrumbs">
            <v-breadcrumbs :items="breadcrumbs" class="pa-0 mb-4">
              <template #divider>
                <lucide-icon name="ChevronRight" :size="16" />
              </template>
            </v-breadcrumbs>
          </slot>
        </div>

        <!-- Page Header -->
        <div v-if="pageTitle || $slots['page-header']" class="app-layout__page-header">
          <slot name="page-header">
            <div class="d-flex justify-space-between align-center mb-6">
              <div>
                <h1 class="text-h4 font-weight-bold mb-2">{{ pageTitle }}</h1>
                <p v-if="pageSubtitle" class="text-body-1 mb-0" style="color: var(--text-secondary)">
                  {{ pageSubtitle }}
                </p>
              </div>
              <div v-if="$slots['page-actions']" class="app-layout__page-actions">
                <slot name="page-actions" />
              </div>
            </div>
          </slot>
        </div>

        <!-- Main Content -->
        <div class="app-layout__content">
          <slot />
        </div>
      </v-container>
        </main>
      </div>
    </v-main>
  </v-app>
</template>

<script lang="ts">
import Vue from 'vue';
import AppHeader from './AppHeader.vue';
import LucideIcon from '../LucideIcon';
import SideNav from './SideNav.vue';
import TopNavStrip from './TopNavStrip.vue';
import type { Product } from '../ProductSwitcher';
import type { TopNavItem } from './TopNavStrip.vue';

export interface SideNavChildItem {
  label: string;
  icon?: string;
  active?: boolean;
  to?: string;
  href?: string;
  action?: string;
}

export interface SideNavItem {
  label: string;
  icon?: string;
  active?: boolean;
  to?: string;
  href?: string;
  action?: string;
  badge?: string | number;
  badgeColor?: string;
  expanded?: boolean;
  header?: string; // Section header text
  children?: SideNavChildItem[];
}

export interface UserMenuItem {
  label: string;
  icon?: string;
  action?: string;
  divider?: boolean;
}

export interface BreadcrumbItem {
  text: string;
  disabled?: boolean;
  href?: string;
  to?: string;
}

export default Vue.extend({
  name: 'AppLayout',

  components: {
    AppHeader,
    LucideIcon,
    SideNav,
    TopNavStrip
  },

  props: {
    // App branding
    logoSrc: {
      type: String,
      default: ''
    },
    darkLogoSrc: {
      type: String,
      default: ''
    },
    logoAlt: {
      type: String,
      default: 'Syrenis Logo',
      validator: (value: string) => value.length <= 100
    },

    // Top navigation
    topNavItems: {
      type: Array as () => TopNavItem[],
      default: () => []
    },

    // Side navigation
    showSideNav: {
      type: Boolean,
      default: true
    },
    sideNavItems: {
      type: Array as () => SideNavItem[],
      default: () => []
    },
    sideNavWidth: {
      type: [Number, String],
      default: 260
    },

    // Product switcher
    products: {
      type: Array as () => Product[],
      default: () => []
    },

    // User menu
    userName: {
      type: String,
      default: 'User',
      validator: (value: string) => value.length <= 100
    },
    userAvatar: {
      type: String,
      default: ''
    },
    userAvatarColor: {
      type: String,
      default: '#406A94'
    },
    userMenuItems: {
      type: Array as () => UserMenuItem[],
      default: () => []
    },

    // Theme switcher control
    showThemeSwitcher: {
      type: Boolean,
      default: false
    },

    // Search/typeahead
    showSearch: {
      type: Boolean,
      default: false
    },
    searchItems: {
      type: Array,
      default: () => []
    },
    searchLoading: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: 'Search pages...'
    },
    searchItemText: {
      type: String,
      default: 'text'
    },
    searchItemValue: {
      type: String,
      default: 'value'
    },

    // Page content
    pageTitle: {
      type: String,
      default: '',
      validator: (value: string) => value.length <= 200
    },
    pageSubtitle: {
      type: String,
      default: '',
      validator: (value: string) => value.length <= 500
    },
    showBreadcrumbs: {
      type: Boolean,
      default: false
    },
    breadcrumbs: {
      type: Array as () => BreadcrumbItem[],
      default: () => []
    },
    fluidContent: {
      type: Boolean,
      default: false
    },
    contentClass: {
      type: String,
      default: ''
    }
  },

  methods: {
    handleTopNavClick(item: TopNavItem) {
      this.$emit('top-nav-click', item);
      if (item.action) {
        this.$emit(item.action);
      }
    },

    handleSideNavClick(item: SideNavItem | SideNavChildItem, event?: Event) {
      this.$emit('sidenav-click', item, event);
      if (item.action) {
        this.$emit(item.action);
      }
    },

    handleSideNavToggle(item: SideNavItem, index: number, expanded: boolean) {
      this.$emit('sidenav-item-toggle', item, index, expanded);
    },

    handleUserMenuClick(item: UserMenuItem) {
      this.$emit('user-menu-click', item);
      if (item.action) {
        this.$emit(item.action);
      }
    },

    handleThemeChange(theme: 'light' | 'dark') {
      this.$emit('theme-change', theme);
    },

    handleProductClick(product: Product) {
      this.$emit('product-click', product);
    },

    handleSearchInput(query: string) {
      this.$emit('search-input', query);
    },

    handleSearchSelect(item: any) {
      this.$emit('search-select', item);
    }
  }
});
</script>

<!-- Global styles for CSS custom properties (must be unscoped to apply :root) -->
<style lang="scss">
@import '@/assets/styles/design-tokens.scss';
</style>

<style lang="scss" scoped>
@import '@/assets/styles/fonts.scss';
@import '@/assets/styles/spacing.scss';

// Screen reader only text
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.app-layout {
  font-family: $font-family-base;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  // Skip to main content link - visually hidden until focused
  &__skip-link {
    position: absolute;
    left: -9999px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
    z-index: 10000;

    &:focus {
      position: fixed;
      top: 0;
      left: 0;
      width: auto;
      height: auto;
      overflow: visible;
      background: var(--primary);
      color: var(--card);
      padding: $spacing-sm $spacing-lg;
      text-decoration: none;
      border-radius: 0 0 $spacing-xs 0;
      font-weight: 500;
      font-size: 14px;
      outline: 2px solid var(--primary);
      outline-offset: 2px;
    }
  }

&__collapse-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: none;
    border: none;
    border-radius: var(--radius);
    color: var(--muted-foreground);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background-color: var(--muted);
      color: var(--foreground);
    }
  }

&__sidenav-header-text {
    padding: $spacing-md $spacing-xs $spacing-sm;
    font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: var(--muted-foreground);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    line-height: 1;
  }

&__sidenav-icon {
    flex-shrink: 0;
    color: inherit;
    opacity: 0.7;
    
    .app-layout__sidenav-item--active & {
      opacity: 1;
    }
  }

&__sidenav-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 18px;
    padding: 0 6px;
    font-size: 10px;
    font-weight: 600;
    color: var(--card);
    background-color: var(--primary);
    border-radius: var(--radius-full);
    margin-left: auto;
  }

&__sidenav-group {
    margin-bottom: 1px;
  }

&__sidenav-footer {
    padding: $spacing-md;
    border-top: 1px solid var(--border);
  }

  &__main {
    background-color: var(--background);
    overflow: visible;
  }

  &__wrapper {
    display: flex;
    max-width: 1800px;
    margin: 0 auto;
    gap: 0;
    overflow: visible;
    align-items: flex-start;
  }

  &__sidenav {
    flex-shrink: 0;
    width: 260px;
    padding-top: $spacing-xl;
  }

  &__content-column {
    flex: 1;
    min-width: 0;
    overflow: visible;
  }

  &__container {
    max-width: none;
    padding: $spacing-xl $spacing-xl $spacing-2xl $spacing-sm;
    overflow: visible;
  }

  &__content {
    overflow: visible;
  }

  &__breadcrumbs {
    margin-bottom: $spacing-lg;

    ::v-deep .v-breadcrumbs {
      padding: 0;
    }

    ::v-deep .v-breadcrumbs__item {
      font-size: 13px;
      color: var(--muted-foreground);

      &:hover {
        color: var(--primary);
      }
    }

    ::v-deep .v-breadcrumbs__divider {
      color: var(--border);
    }

    ::v-deep .v-breadcrumbs__item--disabled {
      color: var(--foreground);
      font-weight: 500;
    }
  }

  &__page-header {
    margin-bottom: $spacing-2xl;

    h1 {
      font-size: 28px;
      font-weight: 700;
      color: var(--foreground);
      letter-spacing: -0.02em;
      margin-bottom: $spacing-xs;
    }

    p {
      font-size: 14px;
      color: var(--muted-foreground);
    }
  }

  &__page-actions {
    display: flex;
    gap: $spacing-sm;
  }
}

</style>
